import { PointerEvent, ReactNode, useEffect, useRef, useState } from "react";
import { Box, IconButton, Modal, styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import PinchOutlinedIcon from "@mui/icons-material/PinchOutlined";
import SwipeOutlinedIcon from "@mui/icons-material/SwipeOutlined";
import { CustomText } from "../../../styles/CustomText";
import {
  GRAYPALEBACKGROUND1,
  GRAYPALEBORDER,
  GRAYPALECONTENTS,
  GRAYPALETITLE,
  GREENCONTENTS,
} from "../../../styles/color";
import { useBackButtonToClose } from "../../../hooks/useBackButtonToClose";
import ControlHints, { HintItem } from "./ControlHints";
import {
  Size,
  ZOOM_IDENTITY,
  ZoomState,
  clampPan,
  pinchZoom,
  tapZoom,
} from "./zoomModel";

interface PhotoLightboxProps {
  title: string;
  subtitle?: string;
  /** SAS 를 붙인, 바로 불러올 수 있는 주소. 없으면 emptyText 를 보여준다 */
  imageUrl?: string;
  downloadName?: string;
  emptyText?: string;
  isMobile: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onClose: () => void;
  /** ←/→ 외에 처리할 키. 처리했으면 true */
  onExtraKey?: (event: KeyboardEvent) => boolean;
  /** 좌우 이동이 무엇인지 (예: "방향 이동", "사진 넘기기") */
  navLabel?: string;
  /** PC 조작법에 더할 항목 (예: 스페이스로 출발/도착 사진 전환) */
  extraHints?: HintItem[];
  /** "2 / 4" 처럼 지금 위치 */
  count?: string;
  /** 하단 왼쪽에 둘 조작 (예: 출발/도착 사진 전환 버튼) */
  toolbar?: ReactNode;
  /** 사진 아래 썸네일 줄 */
  strip?: ReactNode;
}

// 누른 채 이만큼 움직이면 "누르기"가 아니라 "끌기"로 본다
const DRAG_THRESHOLD_PX = 6;
// 원래 크기에서 이만큼 옆으로 밀면 다음/이전 사진
const SWIPE_THRESHOLD_PX = 60;

interface Gesture {
  startX: number;
  startY: number;
  startZoom: ZoomState;
  moved: boolean;
  pinchStartDistance?: number;
  pinchStartZoom?: ZoomState;
}

/**
 * 사진 크게 보기 창. 차량 사진과 휴양소 사진이 같이 쓴다.
 * - 누르면 누른 지점 기준 2배 확대, 다시 누르면 원래 크기. 확대 중에는 끌어서 이동
 * - 휴대폰은 전체 화면, 두 손가락 확대, 원래 크기에서 좌우로 밀면 다음/이전
 * - PC 는 ←/→ 로 넘기기, ESC 로 닫기. 휴대폰·브라우저 뒤로가기로도 닫힌다
 */
function PhotoLightbox({
  title,
  subtitle,
  imageUrl,
  downloadName,
  emptyText = "사진이 없습니다",
  isMobile,
  onPrev,
  onNext,
  onClose,
  onExtraKey,
  navLabel = "사진 넘기기",
  extraHints = [],
  count,
  toolbar,
  strip,
}: PhotoLightboxProps) {
  useBackButtonToClose(true, onClose);

  const [zoom, setZoom] = useState<ZoomState>(ZOOM_IDENTITY);
  const [gesturing, setGesturing] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<Gesture | null>(null);

  // 다른 사진으로 넘어가면 확대를 푼다
  useEffect(() => setZoom(ZOOM_IDENTITY), [imageUrl, title]);

  // 최신 콜백을 키보드 처리에서 쓰기 위해 ref 에 둔다
  const handlers = useRef({ onPrev, onNext, onExtraKey });
  handlers.current = { onPrev, onNext, onExtraKey };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (handlers.current.onExtraKey?.(event)) {
        event.preventDefault();
        return;
      }
      if (event.key === "ArrowLeft") handlers.current.onPrev?.();
      else if (event.key === "ArrowRight") handlers.current.onNext?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const stageSize = (): Size => {
    const rect = stageRef.current?.getBoundingClientRect();
    return { width: rect?.width ?? 0, height: rect?.height ?? 0 };
  };
  // 확대 전 사진이 영역 안에서 차지하는 크기. offset 크기는 transform 의 영향을 받지 않는다.
  const contentSize = (): Size => ({
    width: imageRef.current?.offsetWidth ?? 0,
    height: imageRef.current?.offsetHeight ?? 0,
  });

  const distance = () => {
    const [a, b] = Array.from(pointers.current.values());
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!imageUrl) return;
    try {
      // 영역 밖으로 끌고 나가도 이어서 받는다. 지원하지 않는 환경이면 그냥 넘어간다
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // 무시
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 1) {
      gesture.current = { startX: event.clientX, startY: event.clientY, startZoom: zoom, moved: false };
    } else if (pointers.current.size === 2 && gesture.current) {
      gesture.current.moved = true;
      gesture.current.pinchStartDistance = distance();
      gesture.current.pinchStartZoom = zoom;
    }
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = gesture.current;
    if (!current || !pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size >= 2 && current.pinchStartDistance && current.pinchStartZoom) {
      setGesturing(true);
      setZoom(
        pinchZoom(
          current.pinchStartZoom,
          current.pinchStartDistance,
          distance(),
          stageSize(),
          contentSize()
        )
      );
      return;
    }
    const dx = event.clientX - current.startX;
    const dy = event.clientY - current.startY;
    if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) current.moved = true;
    if (current.moved && current.startZoom.scale > 1) {
      setGesturing(true);
      setZoom(
        clampPan(
          { ...current.startZoom, x: current.startZoom.x + dx, y: current.startZoom.y + dy },
          stageSize(),
          contentSize()
        )
      );
    }
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const current = gesture.current;
    pointers.current.delete(event.pointerId);
    if (!current || pointers.current.size > 0) return;
    gesture.current = null;
    setGesturing(false);

    const dx = event.clientX - current.startX;
    const dy = event.clientY - current.startY;
    if (!current.moved) {
      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect) return;
      const point = {
        x: event.clientX - (rect.left + rect.width / 2),
        y: event.clientY - (rect.top + rect.height / 2),
      };
      setZoom(tapZoom(zoom, point, stageSize(), contentSize()));
    } else if (
      current.startZoom.scale === 1 &&
      current.pinchStartDistance === undefined &&
      Math.abs(dx) > SWIPE_THRESHOLD_PX &&
      Math.abs(dx) > Math.abs(dy)
    ) {
      if (dx < 0) onNext?.();
      else onPrev?.();
    }
  };

  const onPointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size === 0) {
      gesture.current = null;
      setGesturing(false);
    }
  };

  const canNavigate = Boolean(onPrev || onNext);
  const zoomed = zoom.scale > 1;
  const hints: HintItem[] = isMobile
    ? zoomed
      ? [
          { icon: <SwipeOutlinedIcon />, label: "끌어서 이동" },
          { icon: <TouchAppOutlinedIcon />, label: "누르면 원래 크기" },
        ]
      : [
          { icon: <TouchAppOutlinedIcon />, label: "누르면 확대" },
          { icon: <PinchOutlinedIcon />, label: "두 손가락 확대" },
          ...(canNavigate ? [{ icon: <SwipeOutlinedIcon />, label: `밀어서 ${navLabel}` }] : []),
        ]
    : zoomed
    ? [
        { keys: ["끌기"], label: "이동" },
        { keys: ["클릭"], label: "원래 크기" },
        { keys: ["Esc"], label: "닫기" },
      ]
    : [
        { keys: ["클릭"], label: "2배 확대" },
        ...(canNavigate ? [{ keys: ["←", "→"], label: navLabel }] : []),
        ...extraHints,
        { keys: ["Esc"], label: "닫기" },
      ];

  return (
    <Modal open onClose={onClose}>
      <Box sx={isMobile ? MobileFrameStyle : PcFrameStyle}>
        <Header>
          <div style={{ minWidth: 0 }}>
            <CustomText type="subtitle" bold500 color={GRAYPALETITLE}>
              {title}
            </CustomText>
            {subtitle && (
              <CustomText type="small" color={GRAYPALECONTENTS}>
                {subtitle}
              </CustomText>
            )}
          </div>
          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
            {imageUrl && (
              <IconButton
                aria-label="원본 내려받기"
                onClick={() => downloadPhoto(imageUrl, downloadName ?? "photo.jpg")}
              >
                <DownloadIcon />
              </IconButton>
            )}
            <IconButton aria-label="닫기" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </Header>

        <StageWrap>
          <Stage
            ref={stageRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            style={{
              cursor: !imageUrl ? "default" : zoomed ? (gesturing ? "grabbing" : "zoom-out") : "zoom-in",
            }}
          >
            {imageUrl ? (
              <StageImage
                ref={imageRef}
                src={imageUrl}
                alt={title}
                draggable={false}
                style={{
                  transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
                  transition: gesturing ? "none" : "transform 0.15s ease-out",
                }}
              />
            ) : (
              <Empty>
                <ImageNotSupportedIcon sx={{ fontSize: 40, color: GRAYPALECONTENTS }} />
                <CustomText type="subtitle" color={GRAYPALECONTENTS}>
                  {emptyText}
                </CustomText>
              </Empty>
            )}
          </Stage>
          {onPrev && (
            <ArrowButton aria-label="이전" onClick={onPrev} style={{ left: 8 }}>
              <ChevronLeftIcon />
            </ArrowButton>
          )}
          {onNext && (
            <ArrowButton aria-label="다음" onClick={onNext} style={{ right: 8 }}>
              <ChevronRightIcon />
            </ArrowButton>
          )}
        </StageWrap>

        {strip}

        <Footer>
          {toolbar}
          <span style={{ flex: 1 }} />
          {count && (
            <CustomText type="subtitle" bold500 color={GRAYPALETITLE}>
              {count}
            </CustomText>
          )}
        </Footer>
        <ControlHints items={hints} />
      </Box>
    </Modal>
  );
}

export default PhotoLightbox;

/**
 * 원본 내려받기. Blob 저장소가 다른 도메인이라 a[download] 만으로는 새 탭에서 열리기만 하므로
 * 받아서 저장한다. 받지 못하면 새 탭으로 연다.
 */
async function downloadPhoto(url: string, name: string) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const objectUrl = URL.createObjectURL(await response.blob());
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = name;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch {
    window.open(url, "_blank", "noopener");
  }
}

// ---------------------------------------------------------------------------
// 썸네일 줄. 여러 장을 넘겨 보는 경우(추가 사진, 휴양소 사진) 사진 아래에 둔다.
// ---------------------------------------------------------------------------

export interface StripItem {
  key: string | number;
  url: string;
  /** 썸네일 아래에 붙일 짧은 글 (예: "출발") */
  badge?: string;
  label: string;
}

interface ThumbStripProps {
  items: StripItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

export function ThumbStrip({ items, selectedIndex, onSelect }: ThumbStripProps) {
  const selectedRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    selectedRef.current?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [selectedIndex]);
  return (
    <Strip>
      {items.map((item, index) => (
        <StripThumb
          key={item.key}
          ref={index === selectedIndex ? selectedRef : undefined}
          type="button"
          selected={index === selectedIndex}
          onClick={() => onSelect(index)}
          aria-label={item.label}
          aria-current={index === selectedIndex}
        >
          <img src={item.url} alt="" />
          {item.badge && <span>{item.badge}</span>}
        </StripThumb>
      ))}
    </Strip>
  );
}

const PcFrameStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(1100px, 94vw)",
  height: "90vh",
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 2,
  display: "flex",
  flexDirection: "column" as const,
  gap: "10px",
  outline: "none",
};

const MobileFrameStyle = {
  position: "absolute" as const,
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  bgcolor: "background.paper",
  p: "10px 12px",
  display: "flex",
  flexDirection: "column" as const,
  gap: "8px",
  outline: "none",
};

const Header = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "8px",
});

const StageWrap = styled("div")({
  position: "relative",
  flex: 1,
  minHeight: 0,
});

const Stage = styled("div")({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  overflow: "hidden",
  borderRadius: "8px",
  backgroundColor: GRAYPALEBACKGROUND1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  // 확대·이동·밀기를 직접 처리하므로 브라우저 기본 제스처(페이지 확대·스크롤)를 끈다
  touchAction: "none",
  userSelect: "none",
});

const StageImage = styled("img")({
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
  transformOrigin: "center center",
  pointerEvents: "none",
});

const Empty = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "6px",
});

const ArrowButton = styled(IconButton)({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "rgba(255, 255, 255, 0.85)",
  border: `1px solid ${GRAYPALEBORDER}`,
  "&:hover": { backgroundColor: "#fff" },
});

const Footer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "10px",
});

const Strip = styled("div")({
  display: "flex",
  gap: "6px",
  overflowX: "auto",
  paddingBottom: "2px",
});

const StripThumb = styled("button", {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>(({ selected }) => ({
  position: "relative",
  flex: "0 0 auto",
  width: "64px",
  height: "64px",
  padding: 0,
  borderRadius: "6px",
  overflow: "hidden",
  cursor: "pointer",
  border: selected ? `2px solid ${GREENCONTENTS}` : `1px solid ${GRAYPALEBORDER}`,
  opacity: selected ? 1 : 0.75,
  "& img": { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  "& span": {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    fontSize: "0.7rem",
    color: "#fff",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
}));
